import React, { useState } from 'react';
import { Nav, DropdownToggle, DropdownMenu, DropdownItem, Dropdown } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { CoreTracking } from '../../../../models/CoreTracking';
import MapGoogleUserTrackingWizard from './MapGoogleUserTrackingWizard';
import EditGoogleUserTrackingWizard from './EditGoogleUserTrackingWizard';
import RestoreGoogleUserTrackingWizard from './RestoreGoogleUserTrackingWizard';
import InactivateGoogleUserTrackingWizard from './InactivateGoogleUserTrackingWizard';
import UpgradeGoogleUserTrackingWizard from './UpgradeGoogleUserTrackingWizard';
import { useTranslation } from 'react-i18next';

enum GoogleUserTrackingOperation {
    None = 0,
    Edit = 1,
    Inactivate = 2,
    Restore = 3,
    Map = 4,
    Upgrade = 5,
}

interface GoogleUserTrackingOperationsMenuProps {
    tracking: CoreTracking
    onClose?: (success: boolean) => void
    disabled?: boolean
}

const GoogleUserTrackingOperationsMenu: React.FC<GoogleUserTrackingOperationsMenuProps> = (props) => {
    const [action, setAction] = useState(GoogleUserTrackingOperation.None);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const { t } = useTranslation(['schoolAdministration', 'common']);

    if (isSynchedGoogleUserTracking(props.tracking)) {
        
        const close = (success: boolean) => {
        setAction(GoogleUserTrackingOperation.None);

        if (props.onClose) {
            props.onClose(success);
        }
        }

    return <React.Fragment>
        <Nav className="right">
            <Dropdown nav isOpen={dropdownOpen} toggle={() => setDropdownOpen(!dropdownOpen)}>
                <DropdownToggle nav style={{ display: 'inline' }}>
                    <FontAwesomeIcon icon="ellipsis" className="icon" title={t('trackingOperations.general.trackingActions', {ns: 'schoolAdministration'})}  />
                </DropdownToggle>
                <DropdownMenu>
                    {props.tracking.active && <DropdownItem onClick={() => setAction(GoogleUserTrackingOperation.Map)} disabled={props.disabled}>{t('trackingOperations.operations.reconfigure', {ns: 'schoolAdministration'})}</DropdownItem>}
                    {props.tracking.active && <DropdownItem onClick={() => setAction(GoogleUserTrackingOperation.Edit)} disabled={props.disabled}>{t('trackingOperations.operations.edit', {ns: 'schoolAdministration'})}</DropdownItem>}
                    {props.tracking.active && <DropdownItem onClick={() => setAction(GoogleUserTrackingOperation.Inactivate)} disabled={props.disabled}>{t('trackingOperations.operations.inactivate', {ns: 'schoolAdministration'})}</DropdownItem>}
                    {!props.tracking.active && <DropdownItem onClick={() => setAction(GoogleUserTrackingOperation.Restore)} disabled={props.disabled}>{t('trackingOperations.operations.restore', {ns: 'schoolAdministration'})}</DropdownItem>}
                </DropdownMenu>
            </Dropdown>
        </Nav>
        {action === GoogleUserTrackingOperation.Map && <MapGoogleUserTrackingWizard tracking={props.tracking} close={close} />}
        {action === GoogleUserTrackingOperation.Edit && <EditGoogleUserTrackingWizard tracking={props.tracking} close={close} />}
        {action === GoogleUserTrackingOperation.Inactivate && <InactivateGoogleUserTrackingWizard tracking={props.tracking} synched={true} close={close} />}
        {action === GoogleUserTrackingOperation.Restore && <RestoreGoogleUserTrackingWizard tracking={props.tracking} close={close} />}        
    </React.Fragment>;
    }
    if (isGoogleUserTracking(props.tracking)){
            const close = (success: boolean) => {
        setAction(GoogleUserTrackingOperation.None);

        if (props.onClose) {
            props.onClose(success);
        }
    }

    return <React.Fragment>
        <Nav className="right">
            <Dropdown nav isOpen={dropdownOpen} toggle={() => setDropdownOpen(!dropdownOpen)}>
                <DropdownToggle nav style={{ display: 'inline' }}>
                    <FontAwesomeIcon icon="ellipsis" className="icon" title={t('trackingOperations.general.trackingActions', {ns: 'schoolAdministration'})}  />
                </DropdownToggle>
                <DropdownMenu>
                    {props.tracking.active && <DropdownItem onClick={() => setAction(GoogleUserTrackingOperation.Upgrade)} disabled={props.disabled}>{t('trackingOperations.operations.upgrade', {ns: 'schoolAdministration'})}</DropdownItem>}
                    {props.tracking.active && <DropdownItem onClick={() => setAction(GoogleUserTrackingOperation.Inactivate)} disabled={props.disabled}>{t('trackingOperations.operations.inactivate', {ns: 'schoolAdministration'})}</DropdownItem>}
                    {!props.tracking.active && <DropdownItem onClick={() => setAction(GoogleUserTrackingOperation.Restore)} disabled={props.disabled}>{t('trackingOperations.operations.restore', {ns: 'schoolAdministration'})}</DropdownItem>}
                </DropdownMenu>
            </Dropdown>
        </Nav>
        {action === GoogleUserTrackingOperation.Upgrade && <UpgradeGoogleUserTrackingWizard tracking={props.tracking} close={close} />}
        {action === GoogleUserTrackingOperation.Inactivate && <InactivateGoogleUserTrackingWizard tracking={props.tracking} synched={false} close={close} />}
        {action === GoogleUserTrackingOperation.Restore && <RestoreGoogleUserTrackingWizard tracking={props.tracking} close={close} />}
    </React.Fragment>;
    }
    
    else return null;
}

export const isGoogleUserTracking = (tracking: CoreTracking): boolean => {
    if (tracking.sourceName !== 'Google') {
        return false;
    }

    switch (tracking.sourceObjectType) {
        case 'StaffUser': return true;
        case 'StudentUser': return true;
        default: return false;
    }
}

export const isSynchedGoogleUserTracking = (tracking: CoreTracking): boolean => {
    if (tracking.sourceName !== 'Google') {
        return false;
    }

    switch (tracking.sourceObjectType) {
        case 'StaffUser:Synched': return true;
        case 'StudentUser:Synched': return true;
        default: return false;
    }
}

export default GoogleUserTrackingOperationsMenu;
